import { useNode } from "@craftjs/core";
import React, { useEffect, useMemo, useState } from "react";
import TextForm from "./Form";
// import "./Text.scss";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import "../../../FroalaEditor/children/FroalaTextEditor/index.scss";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import { useDispatch, useSelector } from "react-redux";

import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { updateTextStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";

export const Text = ({
  text,
  fontSize,
  textAlign,
  padding,
  isApplyPaddingOnAllSides,
  allSidePadding,
  headingProps,
  paragraphProps,
  lineHeight,
  ...props
}) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));
  const dispatch = useDispatch();
  const { text: textProps } = useSelector((state) => state.codeblockStyles);

  const [editable, setEditable] = useState(false);

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Text);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Don't copy text value, only styles
        props.fontSize = props.fontSize || textProps.fontSize;
        props.padding = props.padding || { ...textProps.padding };
        props.isApplyPaddingOnAllSides =
          props.isApplyPaddingOnAllSides || textProps.isApplyPaddingOnAllSides;
        props.allSidePadding = props.allSidePadding || textProps.allSidePadding;
        props.headingProps = props.headingProps || {
          ...textProps.headingProps,
        };
        props.paragraphProps = props.paragraphProps || {
          ...textProps.paragraphProps,
        };
        props.lineHeight = props.lineHeight || textProps.lineHeight;
      });
    }, 5);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (selected) {
      return;
    }

    setEditable(false);
  }, [selected]);

  const paddingStyle = isApplyPaddingOnAllSides
    ? `${allSidePadding}px`
    : padding
    ? `${padding?.top}px ${padding?.right}px ${padding?.bottom}px ${padding?.left}px`
    : undefined;

  const froalaCssVariables = useMemo(() => {
    const hp = headingProps || {};
    const pp = paragraphProps || {};
    return {
      "--heading-color": hp.fontColor || "black",
      "--heading-family": hp.fontFamily || "Poppins, sans-serif",
      "--heading-size": `${hp.fontSize || 16}px`,
      "--paragraph-color": pp.fontColor || "black",
      "--paragraph-family": pp.fontFamily || "Poppins, sans-serif",
      "--paragraph-size": `${pp.fontSize || 14}px`,
      ...(lineHeight ? { "--line-height": lineHeight } : {}),
    };
  }, [headingProps, paragraphProps, lineHeight]);

  return (
    <div
      {...props}
      ref={(ref) => {
        connect(drag(ref));
      }}
      onClick={() => selected && setEditable(true)}
    >
      <BlockIndicator>
        <div
          className="froalaEditor removeOutlineOnWhiteText"
          style={{
            padding: paddingStyle,
            ...froalaCssVariables,
          }}
        >
          <FroalaEditorView
            model={text || '<p style="color: grey;">Add Text Here</p>'}
          />
        </div>
      </BlockIndicator>
    </div>
  );
};

const TextSettings = () => {
  const {
    actions: { setProp },
    fontSize,
    text,
    padding,
    isApplyPaddingOnAllSides,
    allSidePadding,
    headingProps,
    paragraphProps,
    lineHeight,
  } = useNode((node) => ({
    text: node.data.props.text,
    fontSize: node.data.props.fontSize,
    padding: node.data.props.padding,
    isApplyPaddingOnAllSides: node.data.props.isApplyPaddingOnAllSides,
    allSidePadding: node.data.props.allSideMargin,
    headingProps: node.data.props.headingProps,
    paragraphProps: node.data.props.paragraphProps,
    lineHeight: node.data.props.lineHeight,
  }));
  const dispatch = useDispatch();

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? padding[side] + 1 : padding[side] - 1;
    if (newValue >= 0) handleChange("padding", newValue, side);
  };

  const handleChange = (type, value, side) => {
    dispatch(toggleSegmentSettings(false));
    setProp((props) => {
      // Clone the props to avoid mutating Proxies
      const newProps = {
        ...props,
        padding: { ...props.padding },
        headingProps: { ...props.headingProps },
        paragraphProps: { ...props.paragraphProps },
      };

      switch (type) {
        case "padding":
          if (value >= 0 && value <= 100) {
            props.padding[side] = value;
            newProps.padding[side] = value;
          }
          break;
        case "allSidePadding":
          props.allSidePadding = value;
          newProps.allSidePadding = value;
          break;
        case "paddingCheckbox":
          props.isApplyPaddingOnAllSides = !props.isApplyPaddingOnAllSides;
          newProps.isApplyPaddingOnAllSides = !props.isApplyPaddingOnAllSides;
          break;
        case "lineHeight":
          props.lineHeight = value;
          newProps.lineHeight = value;
          break;
        case "headingProps":
          if (side === "resetKey") {
            props.headingProps.resetKey = props.headingProps.resetKey + 1;
            newProps.headingProps.resetKey = newProps.headingProps.resetKey + 1;
          } else {
            props.headingProps[side] = value;
            newProps.headingProps[side] = value;
          }
          break;
        case "paragraphProps":
          if (side === "resetKey") {
            props.paragraphProps.resetKey = props.paragraphProps.resetKey + 1;
            newProps.paragraphProps.resetKey =
              newProps.paragraphProps.resetKey + 1;
          } else {
            props.paragraphProps[side] = value;
            newProps.paragraphProps[side] = value;
          }
          break;
      }

      // Dispatch the updated props
      dispatch(updateTextStyles(newProps));
    }, 500);
  };

  return (
    <>
      <TextForm
        setProp={setProp}
        fontSize={fontSize}
        text={text}
        padding={padding}
        increasePadding={(side) => handleAdjustment(side, "increase")}
        decreasePadding={(side) => handleAdjustment(side, "decrease")}
        isApplyPaddingOnAllSides={isApplyPaddingOnAllSides}
        allSidePadding={allSidePadding}
        handlePaddingCheckBox={() => handleChange("paddingCheckbox")}
        handleSliderPaddingChange={(value) =>
          handleChange("allSidePadding", value)
        }
        handlePaddingChange={(side, newValue) =>
          handleChange("padding", newValue, side)
        }
        handleLineHeightChange={(value) => handleChange("lineHeight", value)}
        handleHeadingPropsChange={(value, type) =>
          handleChange("headingProps", value, type)
        }
        handleParagraphPropsChange={(value, type) =>
          handleChange("paragraphProps", value, type)
        }
        headingProps={headingProps}
        paragraphProps={paragraphProps}
        lineHeight={lineHeight}
      />
    </>
  );
};

export const TextDefaultProps = {};

Text.craft = {
  props: TextDefaultProps,
  related: {
    settings: TextSettings,
  },
};
